import { Box, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useUser } from '@clerk/react';
import { Button, ConsultationStepper, MedicalDisclaimerModal } from '@/components';
import { useGlobalContext } from '@/store';
import { apiClient } from '@/api/client';
import { useRecaptcha } from '../useRecaptcha';

const STORAGE_KEY_PATIENT = 'homeopathy_patient';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

export function Account() {
  const { patient, setPatient, setToast, startConsultation } = useGlobalContext();
  const { user } = useUser();
  const navigate = useNavigate();
  const { loading: recaptchaLoading, verifyAction } = useRecaptcha();
  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  const [name, setName] = useState(patient.name);
  const [age, setAge] = useState(String(patient.age));
  const [gender, setGender] = useState(patient.gender);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [unfinishedModalOpen, setUnfinishedModalOpen] = useState(false);
  const [unfinishedData, setUnfinishedData] = useState<{ consultationToken: string; age?: number; gender?: string } | null>(null);
  const [canStartNew, setCanStartNew] = useState(true);

  // Clickwrap consent state (mandatory, unchecked by default)
  const [consentMedical, setConsentMedical] = useState(false);
  const [consentSensitive, setConsentSensitive] = useState(false);
  const [disclaimerModalOpen, setDisclaimerModalOpen] = useState(false);

  const sendConsentAuditLog = useCallback(async (medical: boolean, sensitive: boolean) => {
    if (!email) return;
    try {
      await apiClient.post('/patients/consent-audit', {
        user_id: email,
        patient_id: patient.consultationToken ? `${email}_${patient.consultationToken}` : null,
        document_version: 'v1.2-2026',
        consent_medical_disclaimer: medical,
        consent_sensitive_data: sensitive,
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      console.error('Failed to log consent audit trail:', err);
    }
  }, [email, patient.consultationToken]);


  useEffect(() => {
    setName(patient.name);
    setAge(String(patient.age));
    setGender(patient.gender);
  }, [patient.name, patient.age, patient.gender]);

  useEffect(() => {
    if (!email) return;
    apiClient
      .get('/patients/check-status', { params: { email, name: name.trim() } })
      .then((res) => {
        if (typeof res.data?.canStartNew === 'boolean') {
          setCanStartNew(res.data.canStartNew);
        }
      })
      .catch(() => {});
  }, [email, name]);

  const handleSave = useCallback(async () => {
    const ageNum = parseInt(age, 10);
    if (Number.isNaN(ageNum) || ageNum < 0 || ageNum > 150) {
      setToast({ type: 'error', message: 'Age must be between 0 and 150.' });
      return;
    }
    if (!name.trim()) {
      setToast({ type: 'error', message: 'Name is required.' });
      return;
    }
    if (!email) {
      setToast({ type: 'error', message: 'Email not available. Please sign in.' });
      return;
    }

    setSaving(true);
    try {
      try {
        await verifyAction('account_save');
      } catch (recaptchaErr) {
        console.warn('reCAPTCHA verification skipped or failed:', recaptchaErr);
      }

      const newPatient = { email, name: name.trim(), age: ageNum, gender, consultationToken: patient.consultationToken };
      setPatient(newPatient);
      localStorage.setItem(STORAGE_KEY_PATIENT, JSON.stringify(newPatient));
      setTouched(false);
      setToast({ type: 'success', message: 'Personal data saved.' });
    } finally {
      setSaving(false);
    }
  }, [age, email, gender, name, patient.consultationToken, setPatient, setToast, verifyAction]);

  const handleStartConsultation = useCallback(async () => {
    const ageNum = parseInt(age, 10);
    if (Number.isNaN(ageNum) || ageNum < 0 || ageNum > 150) {
      setToast({ type: 'error', message: 'Age must be between 0 and 150.' });
      return;
    }
    if (!name.trim()) {
      setToast({ type: 'error', message: 'Name is required.' });
      return;
    }
    if (!email) {
      setToast({ type: 'error', message: 'Email not available. Please sign in.' });
      return;
    }
    if (!canStartNew) {
      setToast({ type: 'error', message: 'Unvalidated consultation limit reached. Please validate or complete a previous consultation to continue.' });
      return;
    }

    setStarting(true);
    try {
      // Auto-save personal data
      const newPatient = { email, name: name.trim(), age: ageNum, gender, consultationToken: patient.consultationToken };
      setPatient(newPatient);
      try {
        localStorage.setItem(STORAGE_KEY_PATIENT, JSON.stringify(newPatient));
      } catch (_) {}
      setTouched(false);

      try {
        await verifyAction('start_consultation');
      } catch (recaptchaErr) {
        console.warn('reCAPTCHA verification skipped or failed:', recaptchaErr);
      }

      try {
        const res = await apiClient.get('/patients/check-status', {
          params: { email, name: name.trim() },
        });
        if (typeof res.data?.canStartNew === 'boolean') {
          setCanStartNew(res.data.canStartNew);
        }
        if (!res.data?.canStartNew) {
          setToast({ type: 'error', message: 'Unvalidated consultation limit reached. Please validate or complete a previous consultation to continue.' });
          return;
        }
        if (res.data?.hasUnfinished && res.data?.consultationToken) {
          setUnfinishedData({
            consultationToken: res.data.consultationToken,
            age: res.data.age,
            gender: res.data.gender,
          });
          setUnfinishedModalOpen(true);
          return;
        }
      } catch (_) {}

      startConsultation();
      setToast({ type: 'success', message: 'New consultation session started.' });
      navigate('/description');
    } finally {
      setStarting(false);
    }
  }, [age, canStartNew, email, gender, name, navigate, patient.consultationToken, setPatient, startConsultation, setToast, verifyAction]);

  const handleResumeUnfinished = () => {
    if (!unfinishedData?.consultationToken) return;
    const ageNum = parseInt(age, 10) || 18;
    const updatedPatient = {
      email,
      name: name.trim(),
      age: unfinishedData.age ?? ageNum,
      gender: unfinishedData.gender ?? gender,
      consultationToken: unfinishedData.consultationToken,
    };
    setPatient(updatedPatient);
    try {
      localStorage.setItem(STORAGE_KEY_PATIENT, JSON.stringify(updatedPatient));
    } catch (_) {}
    setUnfinishedModalOpen(false);
    setToast({ type: 'success', message: 'Resuming previous consultation.' });
    navigate('/description');
  };

  const handleStartFresh = () => {
    if (!canStartNew) {
      setToast({ type: 'error', message: 'Unvalidated consultation limit reached. Please validate or complete a previous consultation to continue.' });
      return;
    }
    setUnfinishedModalOpen(false);
    startConsultation();
    setToast({ type: 'success', message: 'New consultation session started.' });
    navigate('/description');
  };

  const handleChange = useCallback(() => setTouched(true), []);

  return (
    <div className="p-4 mx-auto max-w-7xl">
      <ConsultationStepper />
      <h1 className="text-2xl font-semibold text-gray-800">Patient Account</h1>
      <p className="mt-1 text-gray-600">
        Enter your name, age, and gender. This information is used in consultations and recommendations.
      </p>

      <Box className="mt-6 max-w-md space-y-4">
        <TextField
          fullWidth
          label="Email"
          value={email}
          disabled
          inputProps={{ 'aria-label': 'Patient email' }}
        />

        <TextField
          fullWidth
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            handleChange();
          }}
          placeholder="e.g. John Smith"
          inputProps={{ 'aria-label': 'Patient name' }}
        />

        <TextField
          fullWidth
          type="number"
          label="Age"
          value={age}
          onChange={(e) => {
            setAge(e.target.value);
            handleChange();
          }}
          inputProps={{
            min: 0,
            max: 150,
            'aria-label': 'Patient age',
          }}
          placeholder="e.g. 35"
        />

        <FormControl fullWidth>
          <InputLabel id="account-gender-label">Gender</InputLabel>
          <Select
            labelId="account-gender-label"
            id="account-gender"
            value={gender}
            label="Gender"
            onChange={(e) => {
              setGender(e.target.value);
              handleChange();
            }}
            aria-label="Patient gender"
          >
            {GENDER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Mandatory Clickwrap Consent Section */}
        <Box className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3 mt-4">
          <Typography variant="subtitle2" className="font-semibold text-slate-800 flex items-center gap-1">
            ⚖️ Legal Consent & Privacy Compliance
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={consentMedical}
                onClick={(e) => {
                  if (!consentMedical) {
                    e.preventDefault();
                    setDisclaimerModalOpen(true);
                  } else {
                    setConsentMedical(false);
                  }
                }}
                onChange={() => {}}
                color="primary"
                id="consent-medical-disclaimer-checkbox"
              />
            }
            label={
              <span className="text-sm text-gray-700">
                I have read and agree to the{' '}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setDisclaimerModalOpen(true);
                  }}
                  className="text-blue-600 underline font-semibold hover:text-blue-800"
                >
                  Medical Disclaimer and Privacy Policy
                </button>
                {!consentMedical ? (
                  <span
                    className="ml-2 inline-flex items-center text-xs font-semibold text-amber-800 bg-amber-100 px-2 py-0.5 rounded cursor-pointer hover:bg-amber-200"
                    onClick={(e) => {
                      e.preventDefault();
                      setDisclaimerModalOpen(true);
                    }}
                  >
                    🔍 (Click link to read & accept terms)
                  </span>
                ) : (
                  <span className="ml-2 text-xs text-emerald-700 font-semibold">✓ Accepted</span>
                )}
                .
              </span>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={consentSensitive}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setConsentSensitive(checked);
                  if (consentMedical && checked) {
                    void sendConsentAuditLog(true, true);
                  }
                }}
                color="primary"
                id="consent-sensitive-data-checkbox"
              />
            }
            label={
              <span className="text-sm text-gray-700">
                I explicitly consent to the processing of my <strong>sensitive health data</strong> (GDPR Special Category / HIPAA PHI) for homeopathic consultation purposes.
              </span>
            }
          />

          {(!consentMedical || !consentSensitive) && (
            <p className="text-xs text-amber-700 font-medium mt-1">
              ⚠️ You must accept both consent agreements before saving or starting a consultation.
            </p>
          )}
        </Box>

        {!canStartNew && (
          <Box className="p-4 bg-amber-50 border border-amber-300 rounded-lg text-amber-900 mt-4 space-y-2">
            <Typography variant="subtitle2" className="font-bold flex items-center gap-1">
              ⚠️ Unvalidated Consultation Limit Reached
            </Typography>
            <Typography variant="body2" className="text-xs text-amber-800">
              You have reached the maximum number of consecutive unvalidated consultations. To start a new consultation, you must resume and complete a previous consultation from history or validate it.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => navigate('/history')}
              className="mt-1 normal-case font-semibold text-xs"
            >
              Go to Consultation History
            </Button>
          </Box>
        )}

        {touched && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleSave()}
            disabled={!consentMedical || !consentSensitive || saving || recaptchaLoading || !name.trim()}
          >
            {saving ? 'Verifying...' : 'Save data'}
          </Button>
        )}

        <Button
          variant="contained"
          color="success"
          onClick={() => void handleStartConsultation()}
          disabled={!consentMedical || !consentSensitive || saving || starting || recaptchaLoading || !canStartNew || !name.trim()}
        >
          {starting ? 'Verifying...' : 'Start New Consultation'}
        </Button>
      </Box>

      {/* Legal Disclaimer Modal */}
      <MedicalDisclaimerModal
        open={disclaimerModalOpen}
        onClose={() => setDisclaimerModalOpen(false)}
        onAccept={() => {
          setConsentMedical(true);
          if (consentSensitive) {
            void sendConsentAuditLog(true, true);
          }
        }}
      />

      <Dialog open={unfinishedModalOpen} onClose={() => setUnfinishedModalOpen(false)}>
        <DialogTitle>Unfinished Consultation Detected</DialogTitle>
        <DialogContent>
          <DialogContentText>
            There is a previous unfinished consultation for patient <strong>{name}</strong>. Would you like to resume the previous consultation or start a new one?
          </DialogContentText>
        </DialogContent>
        <DialogActions className="flex flex-col sm:flex-row gap-2 p-4">
          <Button variant="outlined" color="primary" onClick={handleResumeUnfinished}>
            Resume previous consultation
          </Button>
          <Button variant="contained" color="success" onClick={handleStartFresh} disabled={!canStartNew}>
            Start new consultation
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

